/**
 * Caller-identity adapter registry.
 *
 * Any module (Call Center, future CRM, Bookings, Tickets, Leads …) can
 * register a resolver that maps an E.164 phone number to a caller
 * profile. The Call Center module's `callcenter-resolve-caller` edge
 * fn merges all server-side resolvers + caches the best match in
 * `callcenter.caller_identity_cache`.
 *
 * Client-side usage (e.g. the dialer widget):
 *
 *   import { useResolveCaller } from '@ezhire/sdk';
 *   const { data: caller, isLoading } = useResolveCaller(phone);
 *
 * The client hook calls the edge fn; the edge fn runs the registered
 * server-side resolvers (which live inside its own module bundle and
 * cannot be extended from here). For *client-only* adapters
 * (e.g. enriching a cached match with inline UI data) use
 * `registerClientCallerAdapter`.
 */
import { useQuery } from '@tanstack/react-query';
import { useSupabaseClient } from './hooks/use-supabase';

/** Common subject types the resolver recognizes. Extend as needed. */
export type CallerSubjectType =
  | 'atlas_user'
  | 'customer'
  | 'lead'
  | 'booking_contact'
  | 'ticket_contact'
  | 'unknown';

export interface CallerProfile {
  phone_e164: string;
  subject_type: CallerSubjectType;
  subject_id: string;
  subject_name: string | null;
  avatar_url?: string | null;
  match_score: number; // 0..1
  source: string;       // adapter name
  extra?: Record<string, unknown>;
  /** Past-calls data surfaced by the server resolver. */
  past_calls?: {
    count: number;
    last_at?: string | null;
    last_disposition?: string | null;
  };
  /** Last N conversation artifacts (transcripts, chats) if available. */
  recent_interactions?: Array<{
    type: 'call_transcript' | 'chat' | 'ticket' | 'note';
    at: string;
    summary: string;
    link?: string;
  }>;
}

export interface CallerLookupResult {
  phone_e164: string;
  best_match: CallerProfile | null;
  candidates: CallerProfile[];
  resolved_at: string;
  from_cache: boolean;
}

/** Client-side adapter — runs in-browser, post-resolve, to enrich UI. */
export interface ClientCallerAdapter {
  name: string;
  priority: number;
  enrich: (result: CallerLookupResult) => Promise<CallerLookupResult>;
}

const clientAdapters: ClientCallerAdapter[] = [];

export function registerClientCallerAdapter(a: ClientCallerAdapter): void {
  const idx = clientAdapters.findIndex((x) => x.name === a.name);
  if (idx >= 0) clientAdapters[idx] = a; else clientAdapters.push(a);
  clientAdapters.sort((x, y) => y.priority - x.priority);
}
export function listClientCallerAdapters(): ReadonlyArray<ClientCallerAdapter> {
  return clientAdapters;
}

/** E.164-ish normaliser — kept tiny on purpose. Real normalisation happens
 *  server-side where we also have the caller's country. */
export function normalisePhone(raw: string): string {
  const digits = raw.replace(/[^\d+]/g, '');
  if (!digits) return '';
  if (digits.startsWith('+')) return digits;
  if (digits.startsWith('00')) return '+' + digits.slice(2);
  return '+' + digits;
}

/**
 * React hook — resolve a phone number to a caller profile.
 * Calls the `callcenter-resolve-caller` Supabase edge function, then
 * runs registered client adapters for UI enrichment.
 */
export function useResolveCaller(rawPhone: string | null | undefined, options?: { enabled?: boolean }) {
  const supabase = useSupabaseClient();
  const phone = rawPhone ? normalisePhone(rawPhone) : '';
  return useQuery<CallerLookupResult | null>({
    queryKey: ['caller-identity', phone],
    enabled: (options?.enabled ?? true) && phone.length > 3,
    staleTime: 30_000,
    queryFn: async () => {
      if (!phone) return null;
      const { data, error } = await supabase.functions.invoke('callcenter-resolve-caller', {
        body: { phone },
      });
      if (error) throw error as Error;
      let result = (data as CallerLookupResult | null);
      if (!result) return null;
      for (const a of clientAdapters) {
        try { result = await a.enrich(result); } catch { /* non-fatal */ }
      }
      return result;
    },
  });
}
